/* eslint-disable no-bitwise */
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppSelector} from '../../../hooks/redux';
import {
  amsSensorAddressMask,
  GasMonitoringNode,
} from '../../../interfaces/GasMonitoringNode';
import DataGrid, {DataGridColumn} from '../../common/DataGrid';

interface Props {
  pk: number;
  item?: GasMonitoringNode;
  onFetched?: () => void;
}

const AMSSensorNodeItemAVA = ({pk, item, onFetched}: Props) => {
  /*********/
  /* fetch */
  /*********/
  const ams_nodes = useAppSelector(({assets}) => assets.ams_nodes);
  const rows = item?.avas ?? [];
  const ava_nodes = useMemo(() => {
    const assigned_ava_ids = item?.avas?.map((it) => it?.id);
    return ams_nodes
      ?.filter((it) => !!it?.ava)
      ?.filter((it) => !assigned_ava_ids?.includes(it?.id));
  }, [ams_nodes, item]);

  // Assign AVA
  const [assignAVAID, setAssignAVAID] = useState<string[] | undefined>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [remoteInputAVA, setRemoteInputAVA] = useState(false);
  const [remoteInputActive, setRemoteInputActive] = useState('Input1Low');
  const [remoteInputRelay, setRemoteInputRelay] = useState(0);

  const submitAssign = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await API.patch<GasMonitoringNode>(`${apiBaseUrl}/ams/${pk}/ava`, {
        ava_commtrac_external_id: assignAVAID,
        assign: true,
        remote_input_ava: remoteInputAVA,
        remote_input_active: remoteInputActive,
        remote_input_relay: remoteInputRelay,
      });
      onFetched?.();
      setAssignAVAID([]);
    } catch (err: any) {
      const messages = getMessagesFromApiError(err, 'STRA_d76eb86b');
      setFetchedErrors(messages);
    }
    setIsSubmitting(false);
  }, [assignAVAID, remoteInputAVA, remoteInputActive, remoteInputRelay]);

  const [isUnassiginSubmitting, setIsUnassiginSubmitting] =
    useState<boolean>(false);
  const submitUnAssign = async (ava_id: number) => {
    setIsUnassiginSubmitting(true);
    try {
      await API.patch<GasMonitoringNode>(`${apiBaseUrl}/ams/${pk}/ava`, {
        ava_commtrac_external_id: [ava_id],
        assign: false,
      });
      onFetched?.();
    } catch (err: any) {
      const messages = getMessagesFromApiError(err, 'STRA_61bb5c6f');
      setFetchedErrors(messages);
    }
    setIsUnassiginSubmitting(false);
  };

  const columns: DataGridColumn<GasMonitoringNode>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.id,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.name,
    },
    {
      field: 'commtrac_external_id',
      headerName: 'Network ID',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row?.commtrac_external_id & amsSensorAddressMask,
    },
    {
      field: 'remote_input_ava',
      headerName: 'Remote Input AVA',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) =>
        row?.joinRow?.remote_input_ava === 1 ? 'True' : 'False',
    },
    {
      field: 'remote_input_active',
      headerName: 'Remote Input Active',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) =>
        String(row?.joinRow?.remote_input_active || '')
          .replace(/([a-zA-Z])(\d)/g, '$1 $2')
          .replace(/(\d)([a-zA-Z])/g, '$1 $2'),
    },
    {
      field: 'remote_input_relay',
      headerName: 'Remote Input Relay',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) =>
        row?.joinRow?.remote_input_relay === 0
          ? 'Horn/Strobe'
          : row?.joinRow?.remote_input_relay === 1
            ? 'Horn'
            : row?.joinRow?.remote_input_relay === 2
              ? 'Strobe'
              : '',
    },
    {
      field: 'actions',
      headerName: 'UnAssign',
      width: 80,
      type: 'actions',
      doNotExport: true,
      sxHeader: {textAlign: 'center', p: 0.5},
      sxCell: {textAlign: 'center', p: 0.5},
      renderCell: ({row}) => (
        <IconButton
          color="error"
          onClick={() => {
            submitUnAssign(row.commtrac_external_id);
          }}
        >
          <RemoveCircleIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box display="flex" flexDirection="column" position="relative" gap={3}>
      <Backdrop open={isUnassiginSubmitting} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}{' '}
        </Alert>
      ))}

      <Box display="flex" flexDirection="column" gap={5}>
        {rows?.length > 0 && (
          <DataGrid
            rows={rows}
            columns={columns}
            loading={false}
            pagination
            size="small"
          />
        )}
        <Box display="grid" alignItems="center">
          <Box display="flex">
            <h3>Assign New AVA</h3>
          </Box>

          <Box display="flex" alignItems="center" marginBottom={1}>
            Remote Input AVA
            <Switch
              checked={remoteInputAVA}
              onChange={() => setRemoteInputAVA(!remoteInputAVA)}
            />
          </Box>

          {remoteInputAVA && (
            <Box display="flex" alignItems="center" marginBottom={1}>
              <FormControl fullWidth>
                <InputLabel id="active-input-label">Active Input</InputLabel>
                <Select
                  size="small"
                  labelId="active-input-label"
                  value={remoteInputActive}
                  label="Active Input"
                  onChange={(e) => setRemoteInputActive(e.target.value)}
                >
                  <MenuItem value="Input1Low">Input 1 Low</MenuItem>
                  <MenuItem value="Input1High">Input 1 High</MenuItem>
                  <MenuItem value="Input2Low">Input 2 Low</MenuItem>
                  <MenuItem value="Input2High">Input 2 High</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="relay-label">Relay</InputLabel>
                <Select
                  size="small"
                  labelId="relay-label"
                  value={remoteInputRelay}
                  label="Relay"
                  onChange={(e) => setRemoteInputRelay(Number(e.target.value))}
                >
                  <MenuItem value="0">Horn/Strobe</MenuItem>
                  <MenuItem value="1">Strobe Only</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Box display="flex" alignItems="center">
            <TextField
              fullWidth
              label="AVAs"
              select
              size="small"
              value={assignAVAID}
              SelectProps={{
                multiple: true,
              }}
              onChange={(el) => {
                const v = el.target.value as unknown as string[];
                setAssignAVAID(v);
              }}
            >
              {ava_nodes
                ?.map((it) => ({
                  id: it?.commtrac_external_id,
                  name: it?.name,
                }))
                .map((option) => (
                  <MenuItem key={option.id ?? ''} value={option.id ?? ''}>
                    {option.name}
                  </MenuItem>
                ))}
            </TextField>
            <LoadingButton
              onClick={submitAssign}
              loading={isSubmitting}
              sx={{
                width: '100px',
                height: '40px',
                border: '1px solid',
                marginLeft: '10px',
              }}
            >
              Assign
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      {/* <Box display="flex" justifyContent="space-between">
        <Box display="flex" justifyContent="start" gap={0.5}>
          <Box position="relative">
            <Button onClick={(event) => setAnchorEl(event.currentTarget)}>
              History
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onBackdropClick={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => {}}>Long Term History Report</MenuItem>
              <MenuItem onClick={() => {}}>Short Term History Reports</MenuItem>
              <MenuItem onClick={() => {}}>Location Report</MenuItem>
            </Menu>
          </Box>
          <IconButton
            onClick={(event) => setAnchorElMore(event.currentTarget)}
            size="small"
            color="primary"
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={anchorElMore}
            open={menuOpenMore}
            onBackdropClick={() => setAnchorElMore(null)}
          >
            <MenuItem onClick={() => {}}>
              <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 1.5}} />
              Purge Device
            </MenuItem>
            <MenuItem onClick={() => {}}>
              <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 1.5}} />
              Purge Transaction
            </MenuItem>
          </Menu>

          {fetchedData ? (
            <>
              {onAck && fetchedData?.ack !== '1' ? (
                <Button onClick={() => onAck()}>Acknowledge</Button>
              ) : null}
            </>
          ) : null}
        </Box>
        <Box display="flex" justifyContent="end" gap={0.5}>
          {onCancel ? <Button onClick={() => onCancel()}>Cancel</Button> : null}
          {onEdit ? (
            <Button onClick={() => onEdit()}>Edit Sensor</Button>
          ) : null}
        </Box>
      </Box> */}
    </Box>
  );
};

export default AMSSensorNodeItemAVA;
